<script>
  import { onMount, onDestroy } from "svelte";
  import {
    Page,
    Navbar,
    NavRight,
    NavTitle,
    Link,
    Card,
    CardHeader,
    CardContent,
    Row,
    Col,
    Button,
    Block,
    Badge,
    Icon,
    Popup,
    f7,
    f7ready,
  } from "framework7-svelte";
  import moment from "moment";
  import SoundAlertManager from "../utils/soundAlerts";
  import { handleUnauthorized } from "../js/auth";
  import { ENGINE_URL, BASE_URL, ENABLE_SIMULATION } from "../services/service.config";
  import { terminalStream } from "../services/TerminalStreamService";
  import { fetchUsersDirectory } from "../services/userService";
  import { TradingService } from "../services/tradingService";
  import { formatCapital as formatInvestment, formatTierBadge, isTierUnassigned } from "../utils/formatters";

  export let f7route = undefined;
  export let f7router = undefined;
  import AssignUccTagModal from "../components/AssignUccTagModal.svelte";
  import UserSearchTypeahead from "../components/UserSearchTypeahead.svelte";
  import EnvironmentSwitcher from "../components/EnvironmentSwitcher.svelte";
  import SimulationBanner from "../components/SimulationBanner.svelte";
  import AuditTrailTab from "../components/AuditTrailTab.svelte";
  import AuditClientTimeline from "../components/AuditClientTimeline.svelte";
  import ClientInspectorDrawer from "../components/ClientInspectorDrawer.svelte";
  import OrdersDataGrid from "../components/OrdersDataGrid.svelte";
  import PositionsDataGrid from "../components/PositionsDataGrid.svelte";
  import LiveSignalChartModal from "../components/LiveSignalChartModal.svelte";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  // Real-Time Signal Chart Modal state
  let showChartModal = false;
  let chartSymbol = "";
  let chartScripCode = "";
  let chartUcc = "";

  function handleOpenChart(event) {
    const detail = event.detail || {};
    chartSymbol = detail.symbol || "";
    chartScripCode = detail.scripCode || "";
    chartUcc = detail.ucc || "";
    showChartModal = true;
  }

  // Bulk selection and configuration state (Task 4.7)
  let selectedUccs = new Set();
  let showBulkConfigModal = false;
  let isBulkSubmitting = false;
  let bulkTier = "KEEP";
  let bulkCustomLot = 25;
  let bulkMultiplier = "";
  let bulkAmount = "";
  let bulkDwnAvg = "KEEP";
  let bulkAutoTrade = "KEEP";

  // Manual extra signal state (Task 4.6)
  let showManualSignalModal = false;
  let manualSignalSymbol = "RELIANCE";
  let manualSignalAction = "BUY";
  let manualSignalStrategy = "S1";
  let manualSignalOrderType = "MARKET";
  let manualSignalPrice = 0;
  let isSendingSignal = false;

  let orders = [];
  let positions = [];
  let users = [];
  let userMap = new Map(); // key: UCC / ClientTag -> user object
  let timer;
  let selectedTab = "split"; // "split", "orders", "positions", "mappings", "audit"
  let engineConnected = false;
  let activeDriver = "BSE_BOW";
  let gatewayEnv = "LIVE";
  let lastCheckedTime = null;
  let errorMessage = null;
  let isFetching = false;
  let streamStatus = "DISCONNECTED"; // DISCONNECTED | CONNECTING | CONNECTED | RECONNECTING
  let isHydrated = false;
  let flashingRowIds = new Set();

  // Environment: Permanently locked to LIVE for institutional portal
  let terminalEnv = "LIVE";
  // Modals & User Assignment State
  let showAssignPopup = false;
  let showClientInspector = false;
  let inspectorClientTag = "";
  let inspectorUser = null;

  // Assign modal state
  let assignSelectedUser = null;

  // Re-assign in details modal
  let reassignSearchQuery = "";
  let reassignSelectedUser = null;
  let isReassignDropdownOpen = false;
  let isReassigning = false;

  // --- PHASE 1: DATA GRID CONTROLS, SORTING & ACTION LEASING ---
  let orderSearchQuery = "";
  let orderStatusFilter = "ALL"; // "ALL" | "OPEN" | "FILLED" | "REJECTED"
  let orderSortCol = "time";
  let orderSortDir = "desc";
  let orderPageSize = 100; // 50 | 100 | "ALL"
  const MAX_ORDERS_BUFFER = 1000;

  let posSearchQuery = "";
  let posStatusFilter = "OPEN_ONLY"; // "ALL" | "OPEN_ONLY" | "PROFIT" | "LOSS"
  let posSortCol = "pnl";
  let posSortDir = "asc"; // lowest P&L (worst drawdown) first by default

  // Optimistic action lease set (avoids polling overwrite jitter)
  let pendingActionIds = new Set();
  let leaseTimers = new Map();

  function acquireActionLease(id) {
    if (!id || id === "-") return;
    pendingActionIds.add(id);
    pendingActionIds = new Set(pendingActionIds);
    if (leaseTimers.has(id)) clearTimeout(leaseTimers.get(id));
    leaseTimers.set(
      id,
      setTimeout(() => releaseActionLease(id), 10000)
    );
  }

  function releaseActionLease(id) {
    if (!id) return;
    pendingActionIds.delete(id);
    pendingActionIds = new Set(pendingActionIds);
    if (leaseTimers.has(id)) {
      clearTimeout(leaseTimers.get(id));
      leaseTimers.delete(id);
    }
  }

  function isOrderActive(ord) {
    if (!ord) return false;
    const s = (ord.status || "").toUpperCase();
    return (
      ["PENDING", "OPEN", "TRIGGER_PENDING", "ACK", "CANCELLING..."].includes(s) ||
      pendingActionIds.has(ord.id)
    );
  }

  // Header Sorting Toggles
  function toggleOrderSort(col) {
    if (orderSortCol === col) {
      orderSortDir = orderSortDir === "asc" ? "desc" : "asc";
    } else {
      orderSortCol = col;
      orderSortDir = col === "time" ? "desc" : "asc";
    }
  }

  function togglePosSort(col) {
    if (posSortCol === col) {
      posSortDir = posSortDir === "asc" ? "desc" : "asc";
    } else {
      posSortCol = col;
      posSortDir = col === "pnl" ? "asc" : "desc";
    }
  }

  let orderCounts = { all: 0, open: 0, filled: 0, rejected: 0 };
  let posCounts = { all: 0, open: 0, profit: 0, loss: 0 };
  let totalPnl = 0;
  let openOrders = 0;
  let executedTrades = 0;
  let filteredOrders = [];
  let pagedOrders = [];
  let openOrdersInFiltered = [];
  let filteredPositions = [];
  let openPositionsInFiltered = [];
  let filteredReassignUsers = [];
  let sortedDirectoryUsers = [];
  let directoryTierFilter = "ALL";
  let directorySearchQuery = "";

  $: orderCounts = {
    all: orders.length,
    open: orders.filter((o) => isOrderActive(o)).length,
    filled: orders.filter((o) => (o.status || "").toUpperCase() === "FILLED").length,
    rejected: orders.filter((o) =>
      ["REJECTED", "CANCELLED", "FAILED"].includes((o.status || "").toUpperCase())
    ).length,
  };

  $: totalPnl = positions.reduce((acc, p) => acc + (p.pnl || 0), 0);
  $: openOrders = orderCounts ? orderCounts.open : 0;
  $: executedTrades = orders.filter((o) => (o.status || "").toUpperCase() === "FILLED").length;

  // Filtered & Sorted Orders Pipeline
  $: filteredOrders = orders
    .filter((ord) => {
      const s = (ord.status || "").toUpperCase();
      if (orderStatusFilter === "OPEN" && !isOrderActive(ord)) {
        return false;
      }
      if (orderStatusFilter === "FILLED" && s !== "FILLED") {
        return false;
      }
      if (
        orderStatusFilter === "REJECTED" &&
        !["REJECTED", "CANCELLED", "FAILED"].includes(s)
      ) {
        return false;
      }

      if (orderSearchQuery.trim()) {
        const q = orderSearchQuery.toLowerCase().trim();
        const matchesId = (ord.id || "").toLowerCase().includes(q);
        const matchesClient = (ord.client || "").toLowerCase().includes(q);
        const matchesSymbol = (ord.symbol || "").toLowerCase().includes(q);
        const matchesUserName =
          ord.user && ord.user.fullName
            ? ord.user.fullName.toLowerCase().includes(q)
            : false;
        if (!matchesId && !matchesClient && !matchesSymbol && !matchesUserName) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      let valA, valB;
      if (orderSortCol === "time") {
        valA = a.rawTime || 0;
        valB = b.rawTime || 0;
      } else if (orderSortCol === "qty") {
        valA = a.qty || 0;
        valB = b.qty || 0;
      } else if (orderSortCol === "price") {
        valA = a.price || 0;
        valB = b.price || 0;
      } else if (orderSortCol === "symbol") {
        valA = (a.symbol || "").toLowerCase();
        valB = (b.symbol || "").toLowerCase();
      } else if (orderSortCol === "client") {
        valA = (a.client || "").toLowerCase();
        valB = (b.client || "").toLowerCase();
      } else if (orderSortCol === "side") {
        valA = (a.side || "").toLowerCase();
        valB = (b.side || "").toLowerCase();
      } else if (orderSortCol === "status") {
        valA = (a.status || "").toLowerCase();
        valB = (b.status || "").toLowerCase();
      } else {
        valA = (a.id || "").toLowerCase();
        valB = (b.id || "").toLowerCase();
      }

      if (valA < valB) return orderSortDir === "asc" ? -1 : 1;
      if (valA > valB) return orderSortDir === "asc" ? 1 : -1;
      return 0;
    });

  $: pagedOrders =
    orderPageSize === "ALL"
      ? filteredOrders
      : filteredOrders.slice(0, Number(orderPageSize));
  $: openOrdersInFiltered = filteredOrders.filter((o) => isOrderActive(o));

  // Live Counts for Positions Status Filter Pills
  $: posCounts = {
    all: positions.length,
    open: positions.filter((p) => p.netQty !== 0).length,
    profit: positions.filter((p) => (p.pnl || 0) > 0).length,
    loss: positions.filter((p) => (p.pnl || 0) < 0).length,
  };

  // Filtered & Sorted Positions Pipeline
  $: filteredPositions = positions
    .filter((pos) => {
      if (posStatusFilter === "OPEN_ONLY" && pos.netQty === 0) return false;
      if (posStatusFilter === "PROFIT" && (pos.pnl || 0) <= 0) return false;
      if (posStatusFilter === "LOSS" && (pos.pnl || 0) >= 0) return false;

      if (posSearchQuery.trim()) {
        const q = posSearchQuery.toLowerCase().trim();
        const matchesClient = (pos.client || "").toLowerCase().includes(q);
        const matchesSymbol = (pos.symbol || "").toLowerCase().includes(q);
        const matchesUserName =
          pos.user && pos.user.fullName
            ? pos.user.fullName.toLowerCase().includes(q)
            : false;
        if (!matchesClient && !matchesSymbol && !matchesUserName) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let valA, valB;
      if (posSortCol === "pnl") {
        valA = a.pnl || 0;
        valB = b.pnl || 0;
      } else if (posSortCol === "netQty") {
        valA = a.netQty || 0;
        valB = b.netQty || 0;
      } else if (posSortCol === "buyAvg") {
        valA = a.buyAvg || 0;
        valB = b.buyAvg || 0;
      } else if (posSortCol === "ltp") {
        valA = a.ltp || 0;
        valB = b.ltp || 0;
      } else if (posSortCol === "symbol") {
        valA = (a.symbol || "").toLowerCase();
        valB = (b.symbol || "").toLowerCase();
      } else {
        valA = (a.client || "").toLowerCase();
        valB = (b.client || "").toLowerCase();
      }

      if (valA < valB) return posSortDir === "asc" ? -1 : 1;
      if (valA > valB) return posSortDir === "asc" ? 1 : -1;
      return 0;
    });

  $: openPositionsInFiltered = filteredPositions.filter((p) => p.netQty !== 0);

  // Single In-Row Cancel Order
  async function handleCancelOrder(ord) {
    if (!ord || !ord.id) return;
    const targetId = ord.id;
    if (pendingActionIds.has(targetId)) return;

    acquireActionLease(targetId);
    orders = orders.map((o) =>
      o.id === targetId ? { ...o, status: "CANCELLING..." } : o
    );

    try {
      await TradingService.cancelOrder(ord);
      SoundAlertManager.playChime("ORDER_CANCELLED");
      f7.toast
        .create({
          text: `✓ Cancel requested for order ${targetId}`,
          closeTimeout: 2500,
        })
        .open();
      setTimeout(fetchLiveTerminalData, 400);
    } catch (err) {
      console.error("Cancel order failed:", err);
      releaseActionLease(targetId);
      await fetchLiveTerminalData();
      f7.dialog.alert(`Cancel failed for ${targetId}: ${err.message}`, "Cancel Error");
    }
  }

  // Bulk Cancel Filtered Open Orders
  function confirmBulkCancelFilteredOrders() {
    const targets = openOrdersInFiltered;
    if (targets.length === 0) {
      f7.dialog.alert(
        "No open/pending orders in current filtered view.",
        "No Orders to Cancel"
      );
      return;
    }

    f7.dialog.confirm(
      `Are you sure you want to cancel all <b>${targets.length}</b> visible open orders?`,
      "Bulk Cancel Orders",
      async () => {
        targets.forEach((ord) => acquireActionLease(ord.id));
        orders = orders.map((o) =>
          targets.some((t) => t.id === o.id) ? { ...o, status: "CANCELLING..." } : o
        );

        const { succeeded, failed } = await TradingService.bulkCancelOrders(targets);

        if (succeeded > 0) {
          SoundAlertManager.playChime("ORDER_CANCELLED");
        }

        f7.toast
          .create({
            text: `Bulk Cancel: ${succeeded} requested${failed > 0 ? `, ${failed} failed` : ""}`,
            closeTimeout: 3000,
          })
          .open();

        setTimeout(fetchLiveTerminalData, 400);
      }
    );
  }

  // In-Row Square Off Position
  function confirmSquareOffPosition(pos) {
    if (!pos || pos.netQty === 0) return;
    const ucc = pos.client;
    const isSim = pos.isSimulation || terminalEnv === "SIMULATION";

    f7.dialog.confirm(
      `Square off position for <b>${ucc}</b> (${pos.symbol}, Net Qty: ${pos.netQty})?`,
      "Square Off Confirmation",
      async () => {
        try {
          await TradingService.squareOffPosition({ ucc, symbol: pos.symbol, isSim });
          f7.toast
            .create({
              text: `✓ Square off sent for ${ucc} (${pos.symbol})`,
              closeTimeout: 2500,
            })
            .open();
          setTimeout(fetchLiveTerminalData, 400);
        } catch (e) {
          f7.dialog.alert(e.message, "Square Off Failed");
        }
      }
    );
  }

  // Bulk Square Off Filtered Positions
  function confirmBulkSquareOffFiltered() {
    const targets = openPositionsInFiltered;
    if (targets.length === 0) {
      f7.dialog.alert(
        "No open positions in the current filtered view.",
        "No Positions to Square Off"
      );
      return;
    }

    const isSim = terminalEnv === "SIMULATION";

    f7.dialog.confirm(
      `Are you sure you want to square off all <b>${targets.length}</b> visible open position(s)?`,
      "Bulk Square Off",
      async () => {
        try {
          const { succeeded, failed } = await TradingService.bulkSquareOff({
            positions: targets,
            isSim,
          });

          f7.toast
            .create({
              text: `Bulk Square Off: ${succeeded} position(s) triggered${failed > 0 ? `, ${failed} failed` : ""}`,
              closeTimeout: 3000,
            })
            .open();

          setTimeout(fetchLiveTerminalData, 400);
        } catch (err) {
          f7.dialog.alert(err.message, "Bulk Square Off Failed");
        }
      }
    );
  }

  // Filter users for Re-assign modal
  $: filteredReassignUsers = users.filter((u) => {
    if (!reassignSearchQuery.trim()) return true;
    const q = reassignSearchQuery.toLowerCase();
    return (
      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
      (u.mobile && u.mobile.includes(q)) ||
      (u.pan && u.pan.toLowerCase().includes(q)) ||
      (u.ucc && u.ucc.toLowerCase().includes(q)) ||
      (u.client_tag && u.client_tag.toLowerCase().includes(q))
    );
  }).slice(0, 15);

  $: unassignedClientsCount = users.filter((u) => (u.client_tag || u.ucc) && isTierUnassigned(u.tier)).length;
  $: tierHCount = users.filter((u) => String(u.tier || "").toUpperCase() === "H").length;
  $: tierMCount = users.filter((u) => String(u.tier || "").toUpperCase() === "M").length;
  $: tierLCount = users.filter((u) => String(u.tier || "").toUpperCase() === "L").length;

  $: filteredDirectoryUsers = users.filter((u) => {
    const q = (directorySearchQuery || "").toLowerCase().trim();
    if (q) {
      const matchName = (u.fullName || "").toLowerCase().includes(q);
      const matchTag = (u.client_tag || u.ucc || "").toLowerCase().includes(q);
      if (!matchName && !matchTag) return false;
    }
    if (directoryTierFilter === "H") return String(u.tier || "").toUpperCase() === "H";
    if (directoryTierFilter === "M") return String(u.tier || "").toUpperCase() === "M";
    if (directoryTierFilter === "L") return String(u.tier || "").toUpperCase() === "L";
    if (directoryTierFilter === "CUSTOM") return String(u.tier || "").toUpperCase() === "CUSTOM";
    if (directoryTierFilter === "UNASSIGNED") return isTierUnassigned(u.tier);
    return true;
  });

  // Sorted users for Directory table
  $: sortedDirectoryUsers = [...filteredDirectoryUsers].sort((a, b) => {
    const aHasTag = Boolean(a.client_tag || a.ucc);
    const bHasTag = Boolean(b.client_tag || b.ucc);

    if (aHasTag !== bHasTag) {
      return aHasTag ? -1 : 1;
    }

    const aApproved = (a.account_status || "").toLowerCase() === "approved";
    const bApproved = (b.account_status || "").toLowerCase() === "approved";

    if (aApproved !== bApproved) {
      return aApproved ? -1 : 1;
    }

    const statusCompare = (a.account_status || "").localeCompare(b.account_status || "");
    if (statusCompare !== 0) {
      return statusCompare;
    }

    return (a.fullName || "").localeCompare(b.fullName || "");
  });

  function setTerminalEnv(env) {
    terminalEnv = env;
    sessionStorage.setItem("dealer_terminal_env", env);
    localStorage.setItem("dealer_terminal_env", env);
    orders = [];
    positions = [];
    fetchDbUsers();
    fetchLiveTerminalData();
    terminalStream.disconnect();
    terminalStream.connect({ env: env });
  }

  // Fetch Database Users
  const fetchDbUsers = async (force = false) => {
    try {
      const { users: fetchedUsers, userMap: fetchedMap } = await fetchUsersDirectory(terminalEnv, force);
      users = fetchedUsers;
      userMap = fetchedMap;
    } catch (e) {
      console.error("Failed to load users directory:", e);
    }
  };

  const fetchLiveTerminalData = async () => {
    isFetching = true;
    try {
      if (terminalEnv === "SIMULATION") {
        // --- SIMULATION MODE DATA FETCHING ---
        const posRes = await fetch(`${ENGINE_URL}/sim/positions`);
        if (posRes.ok) {
          const posData = await posRes.json();
          const rawPositions = Array.isArray(posData.positions) ? posData.positions : [];
          positions = rawPositions.map((p) => {
            const clientKey = (p.ucc || "").toUpperCase();
            const tiedUser = userMap.get(clientKey);
            return {
              client: p.ucc || "-",
              user: tiedUser,
              symbol: p.symbol || "-",
              netQty: p.net_qty || 0,
              buyAvg: p.avg_price || 0,
              ltp: p.ltp || 0,
              pnl: (p.unrealized_pnl || 0) + (p.realized_pnl || 0),
              status: p.status || "OPEN",
              isSimulation: true,
            };
          });
        }
      } else {
        // --- LIVE PRODUCTION DATA FETCHING ---
        const posRes = await fetch(`${ENGINE_URL}/positions`);
        if (posRes.ok) {
          const posData = await posRes.json();
          const rawPositions = Array.isArray(posData)
            ? posData
            : posData && Array.isArray(posData.positions)
            ? posData.positions
            : [];
          positions = rawPositions.map((p) => {
            const clientKey = (p.ucc || "").toUpperCase();
            const tiedUser = userMap.get(clientKey);
            return {
              client: p.ucc || "-",
              user: tiedUser,
              symbol: p.symbol || "-",
              netQty: p.filled_qty || p.ordered_qty || 0,
              buyAvg: p.avg_price || 0,
              ltp: p.ltp || 0,
              pnl: (p.unrealized_pnl || 0) + (p.realized_pnl || 0),
              status: p.status || "OPEN",
              isSimulation: false,
            };
          });
        }
      }

      // Fetch Orders
      const orderEndpoint = terminalEnv === "SIMULATION" ? `${ENGINE_URL}/sim/orders` : `${ENGINE_URL}/orders`;
      const orderRes = await fetch(orderEndpoint);
      if (orderRes.ok) {
        const orderData = await orderRes.json();
        let rawOrders = Array.isArray(orderData)
          ? orderData
          : orderData && Array.isArray(orderData.orders)
          ? orderData.orders
          : [];

        // Ring buffer retention: cap to MAX_ORDERS_BUFFER, strictly retaining open orders & newest completed
        if (rawOrders.length > MAX_ORDERS_BUFFER) {
          const active = rawOrders.filter((o) =>
            ["PENDING", "OPEN", "TRIGGER_PENDING", "ACK"].includes((o.status || "").toUpperCase())
          );
          const terminal = rawOrders.filter(
            (o) => !["PENDING", "OPEN", "TRIGGER_PENDING", "ACK"].includes((o.status || "").toUpperCase())
          );
          terminal.sort(
            (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
          );
          const keepTerminalCount = Math.max(0, MAX_ORDERS_BUFFER - active.length);
          rawOrders = [...active, ...terminal.slice(0, keepTerminalCount)];
        }

        orders = rawOrders.map((ord, idx) => {
          const clientKey = (ord.client_id || ord.ucc || "").toUpperCase();
          const tiedUser = userMap.get(clientKey);
          const orderId =
            ord.order_tag ||
            ord.bse_order_no ||
            ord.id ||
            `ORD_ANON_${idx}_${ord.created_at || Date.now()}`;
          const rawStatus = ord.status || "PENDING";

          // If backend already transitioned to CANCELLED / REJECTED / FILLED, clear lease
          if (["CANCELLED", "REJECTED", "FAILED", "FILLED"].includes(rawStatus.toUpperCase())) {
            if (pendingActionIds.has(orderId)) {
              releaseActionLease(orderId);
            }
          }

          // Preserve optimistic lease status if pending cancel action is active
          const displayStatus = pendingActionIds.has(orderId) ? "CANCELLING..." : rawStatus;

          return {
            id: orderId,
            orderTag: ord.order_tag || "",
            bseOrderNo: ord.bse_order_no || "",
            scripCode: ord.scrip_code || "",
            client: ord.client_id || ord.ucc || "-",
            user: tiedUser,
            symbol: ord.symbol || "-",
            side: ord.buy_sell || ord.side || "-",
            qty: ord.quantity || 0,
            price: ord.price || 0,
            status: displayStatus,
            time: ord.created_at ? moment(ord.created_at).format("HH:mm:ss") : "-",
            rawTime: ord.created_at ? new Date(ord.created_at).getTime() : 0,
            isSimulation: Boolean(
              ord.is_simulation || String(ord.order_tag || "").startsWith("SIM_ORD_")
            ),
          };
        });
      }

      // Fetch Gateway Config
      const cfgRes = await fetch(`${ENGINE_URL}/gateway/config`);
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        if (cfg.active_driver) activeDriver = cfg.active_driver;
        if (cfg.environment) gatewayEnv = cfg.environment;
      }

      engineConnected = true;
      errorMessage = null;
      lastCheckedTime = moment().format("HH:mm:ss");
    } catch (err) {
      console.error("Terminal engine connection error:", err);
      orders = [];
      positions = [];
      engineConnected = false;
      errorMessage = err.message || "Failed to communicate with Go trading engine";
      lastCheckedTime = moment().format("HH:mm:ss");
    } finally {
      isFetching = false;
    }
  };

  function flashRow(id) {
    if (!id) return;
    flashingRowIds.add(id);
    flashingRowIds = new Set(flashingRowIds);
    setTimeout(() => {
      flashingRowIds.delete(id);
      flashingRowIds = new Set(flashingRowIds);
    }, 1200);
  }

  function handleStreamOrderUpdate(ordUpdate) {
    if (!ordUpdate) return;
    const targetId = ordUpdate.order_tag || ordUpdate.bse_order_no || ordUpdate.id;
    if (!targetId) return;

    const rawStatus = (ordUpdate.status || "PENDING").toUpperCase();
    if (["CANCELLED", "REJECTED", "FAILED", "FILLED"].includes(rawStatus)) {
      if (pendingActionIds.has(targetId)) {
        releaseActionLease(targetId);
      }
    }

    if (rawStatus === "FILLED") {
      SoundAlertManager.playChime("ORDER_FILLED");
    } else if (rawStatus === "CANCELLED") {
      SoundAlertManager.playChime("ORDER_CANCELLED");
    }

    flashRow(targetId);

    const existingIdx = orders.findIndex(
      (o) => o.id === targetId || (o.orderTag && o.orderTag === ordUpdate.order_tag)
    );

    if (existingIdx !== -1) {
      const current = orders[existingIdx];
      orders[existingIdx] = {
        ...current,
        status: pendingActionIds.has(targetId) ? "CANCELLING..." : (ordUpdate.status || current.status),
        qty: ordUpdate.quantity || ordUpdate.qty || current.qty,
        price: ordUpdate.price !== undefined ? ordUpdate.price : current.price,
      };
      orders = [...orders];
    } else {
      const clientKey = (ordUpdate.client_id || ordUpdate.ucc || "").toUpperCase();
      const tiedUser = userMap.get(clientKey);
      const newOrd = {
        id: targetId,
        orderTag: ordUpdate.order_tag || "",
        bseOrderNo: ordUpdate.bse_order_no || "",
        scripCode: ordUpdate.scrip_code || "",
        client: ordUpdate.client_id || ordUpdate.ucc || "-",
        user: tiedUser,
        symbol: ordUpdate.symbol || "-",
        side: ordUpdate.buy_sell || ordUpdate.side || "-",
        qty: ordUpdate.quantity || ordUpdate.qty || 0,
        price: ordUpdate.price || 0,
        status: pendingActionIds.has(targetId) ? "CANCELLING..." : (ordUpdate.status || "PENDING"),
        time: ordUpdate.created_at ? moment(ordUpdate.created_at).format("HH:mm:ss") : moment().format("HH:mm:ss"),
        rawTime: ordUpdate.created_at ? new Date(ordUpdate.created_at).getTime() : Date.now(),
        isSimulation: Boolean(
          ordUpdate.is_simulation || String(ordUpdate.order_tag || "").startsWith("SIM_ORD_")
        ),
      };
      orders = [newOrd, ...orders.slice(0, MAX_ORDERS_BUFFER - 1)];
    }
  }

  function handleStreamDelta(delta) {
    if (!delta) return;
    if (delta.ucc && delta.symbol) {
      const clientKey = delta.ucc.toUpperCase();
      const posIdx = positions.findIndex(
        (p) => (p.client || "").toUpperCase() === clientKey && p.symbol === delta.symbol
      );
      if (posIdx !== -1) {
        positions[posIdx] = {
          ...positions[posIdx],
          netQty: delta.net_qty !== undefined ? delta.net_qty : positions[posIdx].netQty,
          ltp: delta.ltp !== undefined ? delta.ltp : positions[posIdx].ltp,
          buyAvg: delta.avg_price !== undefined ? delta.avg_price : positions[posIdx].buyAvg,
          pnl:
            delta.total_pnl !== undefined
              ? delta.total_pnl
              : (delta.unrealized_pnl || 0) + (delta.realized_pnl || 0),
        };
        positions = [...positions];
      }
    }
  }

  function handleStreamHeartbeat(hb) {
    engineConnected = true;
    if (hb && hb.active_driver) activeDriver = hb.active_driver;
    if (hb && hb.environment) gatewayEnv = hb.environment;
  }

  let unbindStatus;
  let unbindOrderUpdate;
  let unbindDelta;
  let unbindHeartbeat;
  let unbindSnapshot;
  let unbindHydration;

  onMount(() => {
    f7ready(async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        if (f7router) {
          f7router.navigate("/");
        } else {
          window.location.href = "/";
        }
        return;
      }
      await fetchDbUsers();
      await fetchLiveTerminalData();
      isHydrated = true;

      // Two-Phase Handshake: Initial REST snapshot followed by real-time SSE stream
      unbindStatus = terminalStream.onStatusChange((s) => (streamStatus = s));
      unbindOrderUpdate = terminalStream.on("order_update", handleStreamOrderUpdate);
      unbindDelta = terminalStream.on("delta", handleStreamDelta);
      unbindHeartbeat = terminalStream.on("heartbeat", handleStreamHeartbeat);
      unbindSnapshot = terminalStream.on("snapshot", () => fetchLiveTerminalData());
      unbindHydration = terminalStream.on("hydration", () => fetchLiveTerminalData());

      terminalStream.connect({ env: terminalEnv });

      // Fallback reconciliation poller (every 3s when disconnected, or 10s health check when connected)
      timer = setInterval(() => {
        if (streamStatus !== "CONNECTED") {
          fetchLiveTerminalData();
        }
      }, 3000);
    });
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
    if (unbindStatus) unbindStatus();
    if (unbindOrderUpdate) unbindOrderUpdate();
    if (unbindDelta) unbindDelta();
    if (unbindHeartbeat) unbindHeartbeat();
    if (unbindSnapshot) unbindSnapshot();
    if (unbindHydration) unbindHydration();
    terminalStream.disconnect();
  });

  function triggerFillChime() {
    SoundAlertManager.playChime("ORDER_FILLED");
    f7.toast.create({ text: "Trade Filled (Audio Chime Tested)", closeTimeout: 2000 }).open();
  }

  // Bulk Selection Helpers
  function toggleSelectAll(e) {
    const checked = e.target.checked;
    if (checked) {
      sortedDirectoryUsers.forEach((u) => {
        const ucc = (u.client_tag || u.ucc || "").toUpperCase();
        if (ucc) selectedUccs.add(ucc);
      });
    } else {
      selectedUccs.clear();
    }
    selectedUccs = new Set(selectedUccs);
  }

  function toggleSelectUser(ucc) {
    const clean = (ucc || "").toUpperCase();
    if (selectedUccs.has(clean)) {
      selectedUccs.delete(clean);
    } else {
      selectedUccs.add(clean);
    }
    selectedUccs = new Set(selectedUccs);
  }

  async function handleInlineTierChange(u, newTier) {
    const ucc = u.client_tag || u.ucc;
    if (!ucc) return;
    const oldTier = u.tier;
    const targetTier = newTier === "" ? null : newTier;
    u.tier = targetTier;
    users = [...users];

    try {
      await TradingService.saveClientConfig(
        ucc,
        {
          tier: targetTier,
          custom_lot: Number(u.custom_lot || u.customLot || 25),
          amount: Number(u.amount || u.investment_capital || 250000),
          multiplier: Number(u.multiplier || 1.0),
          dwn_avg_qty: Number(u.dwn_avg_qty || u.dwnAvgQty || 1),
        },
        terminalEnv === "SIMULATION"
      );
      f7.toast.create({
        text: `✓ ${ucc} set to ${targetTier ? 'Tier ' + targetTier : 'Unassigned'}!`,
        closeTimeout: 2000,
      }).open();
    } catch (err) {
      console.error(`Failed to update tier for ${ucc}:`, err);
      u.tier = oldTier;
      users = [...users];
      f7.dialog.alert(err.message || "Failed to update client tier", "Update Failed");
    }
  }

  async function applyQuickBulkTier(tierCode) {
    if (selectedUccs.size === 0) return;
    const targetArray = Array.from(selectedUccs);
    const resolvedTier = tierCode === "NULL" ? null : tierCode;

    users = users.map((u) => {
      const uTag = (u.client_tag || u.ucc || "").toUpperCase();
      if (selectedUccs.has(uTag)) {
        return { ...u, tier: resolvedTier };
      }
      return u;
    });

    try {
      await TradingService.bulkUpdateClientConfig({
        uccs: targetArray,
        config: { tier: resolvedTier },
      });
      f7.toast.create({
        text: `✓ ${targetArray.length} client(s) updated to ${resolvedTier ? 'Tier ' + resolvedTier : 'Unassigned'}!`,
        closeTimeout: 2500,
      }).open();
      selectedUccs.clear();
      selectedUccs = new Set();
    } catch (err) {
      console.error("Quick bulk tier update failed:", err);
      f7.dialog.alert(err.message || "Failed to update clients in bulk", "Bulk Update Failed");
      await fetchDbUsers(true);
    }
  }

  async function executeBulkConfig() {
    if (selectedUccs.size === 0) return;
    isBulkSubmitting = true;
    try {
      const config = {};
      if (bulkTier !== "KEEP") {
        config.tier = bulkTier === "NULL" ? null : bulkTier;
        if (bulkTier === "CUSTOM") config.custom_lot = Number(bulkCustomLot || 25);
      }
      if (bulkMultiplier && !isNaN(Number(bulkMultiplier))) {
        config.multiplier = Number(bulkMultiplier);
      }
      if (bulkAmount && !isNaN(Number(bulkAmount))) {
        config.amount = Number(bulkAmount);
      }
      if (bulkDwnAvg !== "KEEP") {
        config.dwn_avg_qty = Number(bulkDwnAvg);
      }

      const targetArray = Array.from(selectedUccs);
      await TradingService.bulkUpdateClientConfig({
        uccs: targetArray,
        config,
      });

      // If auto trade changed in bulk
      if (bulkAutoTrade !== "KEEP") {
        const enabled = bulkAutoTrade === "ENABLE";
        await Promise.allSettled(
          targetArray.map((u) => TradingService.toggleAutoTrade(u, enabled))
        );
      }

      f7.toast.create({
        text: `✓ Successfully updated ${targetArray.length} clients in bulk!`,
        closeTimeout: 3000,
      }).open();

      selectedUccs.clear();
      selectedUccs = new Set();
      showBulkConfigModal = false;
      await fetchDbUsers(true);
      await fetchLiveTerminalData();
    } catch (err) {
      f7.dialog.alert(err.message, "Bulk Configuration Failed");
    } finally {
      isBulkSubmitting = false;
    }
  }

  async function executeManualSignal() {
    if (!manualSignalSymbol || !manualSignalSymbol.trim()) {
      f7.dialog.alert("Stock symbol is required", "Validation Error");
      return;
    }
    isSendingSignal = true;
    try {
      await TradingService.sendManualSignal({
        symbol: manualSignalSymbol.trim().toUpperCase(),
        action: manualSignalAction,
        strategy: manualSignalStrategy,
        order_type: manualSignalOrderType,
        price: manualSignalPrice ? Number(manualSignalPrice) : 0,
      });

      f7.toast.create({
        text: `⚡ Manual ${manualSignalAction} signal fired for ${manualSignalSymbol}!`,
        closeTimeout: 3000,
      }).open();
      showManualSignalModal = false;
      setTimeout(fetchLiveTerminalData, 600);
    } catch (e) {
      f7.dialog.alert(e.message, "Signal Dispatch Failed");
    } finally {
      isSendingSignal = false;
    }
  }

  async function handleEmergencyScripSquareOff(symbol) {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) return;
    f7.dialog.confirm(
      `Are you sure you want to trigger an <b>Emergency Square-Off</b> for <b>${sym}</b> across <b>ALL clients</b>?<br/><span style="color:#ef4444; font-size:12px;">Market exit orders will fire concurrently for every open position in ${sym}.</span>`,
      `Emergency Square Off [${sym}]`,
      async () => {
        try {
          await TradingService.squareOffScrip(sym);
          f7.toast.create({
            text: `⚡ Emergency square-off dispatched for ${sym}!`,
            closeTimeout: 3000,
          }).open();
          setTimeout(fetchLiveTerminalData, 600);
        } catch (e) {
          f7.dialog.alert(e.message, "Square-Off Failed");
        }
      }
    );
  }

  // Quick 1-Click Auto-Trade Toggle with Unassigned Tier Interlock
  async function toggleAutoTrade(user) {
    const ucc = user.client_tag || user.ucc;
    if (!ucc) {
      f7.dialog.alert("Please assign a UCC tag first before enabling auto-trade.", "UCC Required");
      return;
    }

    const nextState = !user.master_opt_in;

    // Safety interlock: warn if toggling ON without an assigned tier
    if (nextState && isTierUnassigned(user.tier)) {
      f7.dialog.confirm(
        `⚠️ Tier Required: Client <b>${ucc}</b> has no assigned Tier (H, M, L, or Custom). Enabling Auto-Trade without a Tier will leave execution NON-OPERATIONAL. Do you wish to continue?`,
        "Tier Required Warning",
        () => executeToggleAutoTrade(user, ucc, nextState)
      );
      return;
    }

    await executeToggleAutoTrade(user, ucc, nextState);
  }

  async function executeToggleAutoTrade(user, ucc, nextState) {
    try {
      await TradingService.toggleAutoTrade(ucc, nextState, null, user.multiplier);
      user.master_opt_in = nextState;
      users = [...users];
      if (userMap.has(ucc.toUpperCase())) {
        userMap.get(ucc.toUpperCase()).master_opt_in = nextState;
      }
      userMap = new Map(userMap);

      f7.toast.create({
        text: `Auto-Trade ${nextState ? "🟢 ENABLED" : "⚪ DISABLED"} for ${ucc}`,
        closeTimeout: 2500,
      }).open();
    } catch (e) {
      f7.dialog.alert(e.message, "Auto-Trade Toggle Failed");
    }
  }

  // Open Slide-Over Client Quick Inspector
  function openClientDetails(clientTag, user) {
    if (!clientTag || clientTag === "-") return;
    const key = (clientTag || "").toUpperCase();
    const resolvedUser = user || userMap.get(key) || null;
    inspectorClientTag = key;
    inspectorUser = resolvedUser;
    showClientInspector = true;
  }

  // Open Assign UCC Tag Modal
  function openAssignModal(prefillUser = null) {
    assignSelectedUser = prefillUser;
    showAssignPopup = true;
  }

  async function handleAssignSuccess() {
    await fetchDbUsers();
    await fetchLiveTerminalData();
  }
</script>

<Page name="dealer-terminal" class="dealer-terminal-page dealer-terminal-container {terminalEnv === 'SIMULATION' ? 'env-simulation' : 'env-live'}">
  <TradingNavbar title="Dealer Terminal">
    <div style="display:flex; align-items:center; gap:8px; margin-right:8px;">
      <!-- Reusable Institutional Live Environment Indicator -->
      <EnvironmentSwitcher
        environment={terminalEnv}
        connected={engineConnected}
      />

      <Button small fill color="purple" onClick={() => (showManualSignalModal = true)}>
        ⚡ Manual Signal
      </Button>
      <Button small fill color="blue" onClick={() => openAssignModal()}>
        <Icon f7="person_crop_circle_badge_plus" size="16" /> &nbsp; Assign UCC Tag
      </Button>
      <Button small outline color="blue" onClick={fetchLiveTerminalData} disabled={isFetching}>
        {isFetching ? "Syncing..." : "Refresh"}
      </Button>
    </div>
    <Link iconF7="waveform_path" tooltip="Test Sound Alert" onClick={triggerFillChime} />
  </TradingNavbar>

    <Block>
      {#if errorMessage}
        <Card class="bg-color-red text-color-white margin-bottom">
          <CardContent>
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div>
                <strong>⚠️ Engine Connection Failure</strong>
                <div style="font-size: 13px; margin-top: 4px;">{errorMessage}</div>
                <div style="font-size: 11px; opacity: 0.85; margin-top: 2px;">
                  Endpoint: {ENGINE_URL} | Last Attempt: {lastCheckedTime || 'N/A'}
                </div>
              </div>
              <Button fill small color="white" text-color="red" onClick={fetchLiveTerminalData}>
                Retry Connection
              </Button>
            </div>
          </CardContent>
        </Card>
      {/if}

      <!-- Reusable Prominent Glowing Simulation Banner -->
      {#if ENABLE_SIMULATION && terminalEnv === 'SIMULATION'}
        <SimulationBanner
          clientCount={users.filter(u => u.client_tag || u.ucc).length}
        />
      {/if}

      <!-- High-Density Pro-Terminal Header Strip -->
      <div class="terminal-stats-bar margin-bottom">
        <div class="stats-group">
          <div class="stat-item">
            <span class="stat-k">Aggregate Client P&L <span style="font-size:10px; color:#888;">(All Clients)</span></span>
            <span class="stat-v {(totalPnl || 0) >= 0 ? 'text-green' : 'text-red'}">
              {(totalPnl || 0) >= 0 ? '+' : ''}₹{(Number(totalPnl) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-k">Total Open Orders</span>
            <span class="stat-v text-blue">{openOrders}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-k">Total Executed Trades</span>
            <span class="stat-v text-cyan">{executedTrades}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-k">Active Positions</span>
            <span class="stat-v text-cyan">
              {posCounts ? posCounts.open : 0}
              {#if posCounts && posCounts.open > 0}
                <span style="font-size:11px; font-weight:600; color:#888; margin-left:4px;">
                  (<span class="text-green">+{posCounts.profit}</span> / <span class="text-red">-{posCounts.loss}</span>)
                </span>
              {/if}
            </span>
          </div>
        </div>

        <!-- Integrated Navigation Segmented Tabs -->
        <div class="terminal-tabs">
          <button class="t-tab {selectedTab === 'split' ? 'active' : ''}" on:click={() => (selectedTab = 'split')}>
            ⚡ Split View
          </button>
          <button class="t-tab {selectedTab === 'orders' ? 'active' : ''}" on:click={() => (selectedTab = 'orders')}>
            📑 Orders ({orders.length})
          </button>
          <button class="t-tab {selectedTab === 'positions' ? 'active' : ''}" on:click={() => (selectedTab = 'positions')}>
            📊 Positions ({positions.length})
          </button>
          <button class="t-tab {selectedTab === 'mappings' ? 'active' : ''}" on:click={() => (selectedTab = 'mappings')}>
            👥 UCC Directory ({users.filter(u => u.client_tag || u.ucc).length})
          </button>
          <button class="t-tab {selectedTab === 'audit' ? 'active' : ''}" on:click={() => (selectedTab = 'audit')}>
            📜 Audit Trail
          </button>
        </div>
      </div>

      <!-- Tab 1: Dual Split View (Orders 55% + Positions 45%) -->
      {#if selectedTab === 'split'}
        <div class="terminal-split-view-container">
          <div class="split-col-orders">
            <OrdersDataGrid
              {orders}
              {filteredOrders}
              {pagedOrders}
              {orderCounts}
              {openOrdersInFiltered}
              bind:orderSearchQuery
              bind:orderStatusFilter
              {orderSortCol}
              {orderSortDir}
              {orderPageSize}
              {pendingActionIds}
              {flashingRowIds}
              {engineConnected}
              {terminalEnv}
              isSplit={true}
              on:sort={(e) => toggleOrderSort(e.detail)}
              on:cancelOrder={(e) => handleCancelOrder(e.detail)}
              on:bulkCancel={confirmBulkCancelFilteredOrders}
              on:openClient={(e) => openClientDetails(e.detail.client, e.detail.user)}
            />
          </div>
          <div class="split-col-positions">
            <PositionsDataGrid
              {positions}
              {filteredPositions}
              {posCounts}
              {openPositionsInFiltered}
              bind:posSearchQuery
              bind:posStatusFilter
              {posSortCol}
              {posSortDir}
              {flashingRowIds}
              {terminalEnv}
              isSplit={true}
              on:sort={(e) => togglePosSort(e.detail)}
              on:squareOffPosition={(e) => confirmSquareOffPosition(e.detail)}
              on:squareOffScrip={(e) => handleEmergencyScripSquareOff(e.detail)}
              on:bulkSquareOff={confirmBulkSquareOffFiltered}
              on:openClient={(e) => openClientDetails(e.detail.client, e.detail.user)}
              on:openChart={handleOpenChart}
            />
          </div>
        </div>

      <!-- Tab 2: Orders Full Width -->
      {:else if selectedTab === 'orders'}
        <OrdersDataGrid
          {orders}
          {filteredOrders}
          {pagedOrders}
          {orderCounts}
          {openOrdersInFiltered}
          bind:orderSearchQuery
          bind:orderStatusFilter
          {orderSortCol}
          {orderSortDir}
          bind:orderPageSize
          {pendingActionIds}
          {flashingRowIds}
          {engineConnected}
          {terminalEnv}
          isSplit={false}
          on:sort={(e) => toggleOrderSort(e.detail)}
          on:cancelOrder={(e) => handleCancelOrder(e.detail)}
          on:bulkCancel={confirmBulkCancelFilteredOrders}
          on:openClient={(e) => openClientDetails(e.detail.client, e.detail.user)}
        />

      <!-- Tab 3: Positions Full Width -->
      {:else if selectedTab === 'positions'}
        <PositionsDataGrid
          {positions}
          {filteredPositions}
          {posCounts}
          {openPositionsInFiltered}
          bind:posSearchQuery
          bind:posStatusFilter
          {posSortCol}
          {posSortDir}
          {flashingRowIds}
          {terminalEnv}
          isSplit={false}
          on:sort={(e) => togglePosSort(e.detail)}
          on:squareOffPosition={(e) => confirmSquareOffPosition(e.detail)}
          on:squareOffScrip={(e) => handleEmergencyScripSquareOff(e.detail)}
          on:bulkSquareOff={confirmBulkSquareOffFiltered}
          on:openClient={(e) => openClientDetails(e.detail.client, e.detail.user)}
          on:openChart={handleOpenChart}
        />

      <!-- Tab 4: UCC Directory -->
      {:else if selectedTab === 'mappings'}
        <div class="card data-table terminal-grid-card">
          <div class="card-header display-flex justify-content-space-between align-items-center">
            <div>
              <strong>Assigned UCC Directory &amp; Auto-Trading</strong>
              <span style="font-size: 12px; color: #64748b; margin-left: 8px;">
                Showing {sortedDirectoryUsers.length} of {users.length} clients
              </span>
            </div>
            <div style="display: flex; gap: 8px; align-items: center;">
              {#if unassignedClientsCount > 0}
                <span class="badge color-red font-bold" style="font-size: 11px;">
                  ⚠️ {unassignedClientsCount} Unassigned
                </span>
              {/if}
              <Button fill small color="blue" onClick={() => openAssignModal()}>
                + Assign UCC Tag
              </Button>
            </div>
          </div>

          <!-- Quick Directory Search & Filter Bar -->
          <div class="directory-toolbar">
            <input
              type="text"
              placeholder="Search by UCC or Client Name..."
              class="dir-search-box"
              bind:value={directorySearchQuery}
            />
            <div class="dir-filter-chips">
              <button
                type="button"
                class="dir-chip {directoryTierFilter === 'ALL' ? 'active' : ''}"
                on:click={() => (directoryTierFilter = 'ALL')}
              >
                All ({users.length})
              </button>
              <button
                type="button"
                class="dir-chip {directoryTierFilter === 'H' ? 'active' : ''}"
                on:click={() => (directoryTierFilter = 'H')}
              >
                🔴 Tier H ({tierHCount})
              </button>
              <button
                type="button"
                class="dir-chip {directoryTierFilter === 'M' ? 'active' : ''}"
                on:click={() => (directoryTierFilter = 'M')}
              >
                🟡 Tier M ({tierMCount})
              </button>
              <button
                type="button"
                class="dir-chip {directoryTierFilter === 'L' ? 'active' : ''}"
                on:click={() => (directoryTierFilter = 'L')}
              >
                🟢 Tier L ({tierLCount})
              </button>
              {#if unassignedClientsCount > 0}
                <button
                  type="button"
                  class="dir-chip alert-chip {directoryTierFilter === 'UNASSIGNED' ? 'active' : ''}"
                  on:click={() => (directoryTierFilter = 'UNASSIGNED')}
                >
                  ⚠️ Unassigned ({unassignedClientsCount})
                </button>
              {/if}
            </div>
          </div>
          <div class="card-content" style="padding:0; overflow-x: auto; max-height: calc(100vh - 280px); overflow-y: auto;">
            <table class="terminal-compact-table">
              <thead>
                <tr>
                  <th class="label-cell" style="width: 32px; text-align: center;">
                    <input
                      type="checkbox"
                      on:change={toggleSelectAll}
                      checked={sortedDirectoryUsers.length > 0 && selectedUccs.size === sortedDirectoryUsers.length}
                    />
                  </th>
                  <th class="label-cell">UCC Tag</th>
                  <th class="label-cell">Full Name</th>
                  <th class="label-cell">F1F2 Tier</th>
                  <th class="label-cell">Multiplier</th>
                  <th class="label-cell">Capital Amount</th>
                  <th class="label-cell">Auto-Trading</th>
                  <th class="label-cell">Account Status</th>
                  <th class="label-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {#if sortedDirectoryUsers.length === 0}
                  <tr>
                    <td colspan="9" style="text-align:center; padding: 28px; color: #94a3b8;">
                      No users loaded from database
                    </td>
                  </tr>
                {:else}
                  {#each sortedDirectoryUsers as u (u.user_id || u._id || u.ucc || u.client_tag)}
                    <tr class="{u.master_opt_in && isTierUnassigned(u.tier) ? 'row-unassigned-alert' : ''}">
                      <td class="label-cell" style="width: 32px; text-align: center;">
                        <input
                          type="checkbox"
                          checked={selectedUccs.has((u.client_tag || u.ucc || '').toUpperCase())}
                          on:change={() => toggleSelectUser(u.client_tag || u.ucc)}
                        />
                      </td>
                      <td class="label-cell">
                        <button
                          type="button"
                          class="client-pill-btn"
                          on:click={() => openClientDetails(u.client_tag || u.ucc, u)}
                          title="Inspect Client Drawer"
                        >
                          <span class="client-tag-badge">
                            {u.client_tag || u.ucc || "-"}
                          </span>
                        </button>
                      </td>
                      <td class="label-cell font-bold">{u.fullName || "-"}</td>
                      <td class="label-cell">
                        <select
                          class="inline-tier-select tier-{isTierUnassigned(u.tier) ? 'unassigned' : String(u.tier || '').toLowerCase()}"
                          value={isTierUnassigned(u.tier) ? '' : String(u.tier).toUpperCase()}
                          on:change={(e) => handleInlineTierChange(u, e.target.value)}
                        >
                          <option value="">⚠️ UNASSIGNED</option>
                          <option value="H">🔴 Tier H</option>
                          <option value="M">🟡 Tier M</option>
                          <option value="L">🟢 Tier L</option>
                          <option value="CUSTOM">⚪ Custom ({u.custom_lot || u.customLot || 25}sh)</option>
                        </select>
                      </td>
                      <td class="label-cell">
                        <span class="font-mono-tabular font-bold text-purple" style="font-size: 13px;">
                          {Number(u.multiplier || 1.0).toFixed(1)}x
                        </span>
                      </td>
                      <td class="label-cell">
                        <strong style="color: #0284c7; font-size: 13px;" class="font-mono-tabular">
                          {formatInvestment(u.amount || u.investment_capital)}
                        </strong>
                      </td>
                      <td class="label-cell">
                        {#if u.master_opt_in && isTierUnassigned(u.tier)}
                          <div style="display:flex; flex-direction:column; gap:2px; align-items:flex-start;">
                            <span class="badge font-bold" style="background:#ef4444; color:#ffffff; font-size:10px;" title="Admin must set Tier before trading starts">
                              ⚠️ NON-OP
                            </span>
                            <button
                              type="button"
                              class="auto-trade-btn inactive"
                              style="font-size:9px; padding:2px 6px;"
                              on:click={() => toggleAutoTrade(u)}
                            >
                              Turn OFF
                            </button>
                          </div>
                        {:else}
                          <button
                            type="button"
                            class="auto-trade-btn {u.master_opt_in ? 'active' : 'inactive'}"
                            on:click={() => toggleAutoTrade(u)}
                            title="Click to toggle Automated Algorithmic Trading authorization"
                          >
                            {u.master_opt_in ? "🟢 Active" : "⚪ OFF"}
                          </button>
                        {/if}
                      </td>
                      <td class="label-cell">
                        <span class="badge {u.account_status === 'approved' ? 'color-green' : 'color-orange'}">
                          {u.account_status || 'pending'}
                        </span>
                      </td>
                      <td class="label-cell">
                        <div style="display:flex; gap: 6px; align-items: center;">
                          <Button
                            small
                            fill={isTierUnassigned(u.tier)}
                            outline={!isTierUnassigned(u.tier)}
                            color={isTierUnassigned(u.tier) ? "red" : "blue"}
                            onClick={() => openClientDetails(u.client_tag || u.ucc, u)}
                          >
                            {isTierUnassigned(u.tier) ? "⚙️ Assign Tier" : "⚙️ Config"}
                          </Button>
                          <Link
                            href={`/user-trading-controls/?ucc=${encodeURIComponent(u.client_tag || u.ucc)}&env=${terminalEnv}`}
                            class="button button-small button-fill button-round"
                          >
                            Controls
                          </Link>
                        </div>
                      </td>
                    </tr>
                  {/each}
                {/if}
              </tbody>
            </table>
          </div>
        </div>

        <!-- Floating Bulk Action Bar -->
        {#if selectedUccs.size > 0}
          <div class="bulk-floating-bar">
            <span class="bulk-selected-count">⚙️ <strong>{selectedUccs.size}</strong> Client(s) Selected</span>
            <div style="display: flex; gap: 6px; align-items: center;">
              <span style="font-size: 11px; color: #94a3b8; font-weight: 600;">Set Tier:</span>
              <button type="button" class="quick-bulk-btn tier-h" title="Set all selected to Tier H" on:click={() => applyQuickBulkTier("H")}>
                🔴 H
              </button>
              <button type="button" class="quick-bulk-btn tier-m" title="Set all selected to Tier M" on:click={() => applyQuickBulkTier("M")}>
                🟡 M
              </button>
              <button type="button" class="quick-bulk-btn tier-l" title="Set all selected to Tier L" on:click={() => applyQuickBulkTier("L")}>
                🟢 L
              </button>
              <button type="button" class="quick-bulk-btn tier-off" title="Reset all selected to Unassigned" on:click={() => applyQuickBulkTier("NULL")}>
                ⚠️ Clear
              </button>

              <div style="width: 1px; height: 16px; background: #475569; margin: 0 4px;"></div>

              <Button fill color="blue" small onClick={() => (showBulkConfigModal = true)}>
                ⚙️ Detailed Bulk Setup...
              </Button>
              <Button outline color="white" small onClick={() => { selectedUccs.clear(); selectedUccs = new Set(); }}>
                Deselect
              </Button>
            </div>
          </div>
        {/if}

      <!-- Tab 5: Audit Trail -->
      {:else if selectedTab === 'audit'}
        <AuditTrailTab
          environment={terminalEnv}
          {users}
        />
      {/if}
    </Block>

  <!-- POPUP 1: ASSIGN UCC TAG TO USER -->
  <AssignUccTagModal
    bind:opened={showAssignPopup}
    user={assignSelectedUser}
    {users}
    lockUser={Boolean(assignSelectedUser)}
    on:success={handleAssignSuccess}
  />

  <!-- SLIDE-OVER CLIENT QUICK INSPECTOR DRAWER -->
  <ClientInspectorDrawer
    bind:opened={showClientInspector}
    clientTag={inspectorClientTag}
    user={inspectorUser}
    {positions}
    {orders}
    {users}
    environment={terminalEnv}
    {pendingActionIds}
    on:close={() => (showClientInspector = false)}
    on:refresh={fetchLiveTerminalData}
    on:update={fetchLiveTerminalData}
    on:cancelOrder={(e) => handleCancelOrder(e.detail)}
    on:squareOffPosition={(e) => confirmSquareOffPosition(e.detail)}
    on:reassign={handleAssignSuccess}
    on:openChart={handleOpenChart}
  />

  <!-- REAL-TIME INTERACTIVE SIGNAL CHART MODAL -->
  <LiveSignalChartModal
    isOpen={showChartModal}
    symbol={chartSymbol}
    scripCode={chartScripCode}
    ucc={chartUcc}
    onClose={() => (showChartModal = false)}
  />

  <!-- POPUP 2: BULK CONFIGURE CLIENTS (Task 4.7) -->
  <Popup class="bulk-config-popup" opened={showBulkConfigModal} onPopupClosed={() => (showBulkConfigModal = false)}>
    <Page>
      <Navbar title={`⚙️ Bulk Configure Clients (${selectedUccs.size})`}>
        <NavRight>
          <Link popupClose>Close</Link>
        </NavRight>
      </Navbar>
      <Block>
        <p class="text-color-gray">
          Apply F1F2 native tier, multiplier, capital amount, or auto-trade settings in bulk across {selectedUccs.size} selected client(s).
        </p>
        <div class="card data-table" style="background:#0f172a; border: 1px solid #1e293b;">
          <div class="card-content card-content-padding" style="padding:16px;">
            <div style="display:flex; flex-direction:column; gap:14px;">
              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">F1F2 Client Tier:</label>
                <select class="form-select" bind:value={bulkTier} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;">
                  <option value="KEEP">— Keep Current Tier —</option>
                  <option value="H">🔴 Tier H (High)</option>
                  <option value="M">🟡 Tier M (Medium)</option>
                  <option value="L">🟢 Tier L (Low)</option>
                  <option value="CUSTOM">⚪ Custom Lot Size</option>
                  <option value="NULL">⚠️ Clear Tier (Unassigned)</option>
                </select>
              </div>

              {#if bulkTier === 'CUSTOM'}
                <div>
                  <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Custom Lot Size (Shares):</label>
                  <input type="number" min="1" step="1" class="form-input" bind:value={bulkCustomLot} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;" />
                </div>
              {/if}

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Multiplier Factor (0.1x – 10.0x):</label>
                <input type="number" step="0.1" min="0.1" max="10.0" placeholder="e.g. 1.0 (Leave blank to keep current)" class="form-input" bind:value={bulkMultiplier} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;" />
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Designated Capital Amount (₹):</label>
                <input type="number" step="10000" placeholder="e.g. 500000 (Leave blank to keep current)" class="form-input" bind:value={bulkAmount} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;" />
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Down-Avg Factor (dwnAvg_qty):</label>
                <select class="form-select" bind:value={bulkDwnAvg} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;">
                  <option value="KEEP">— Keep Current Factor —</option>
                  <option value="1">1x (Same as Base Qty)</option>
                  <option value="2">2x (Double Lot on Dips)</option>
                </select>
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Auto-Trade Authorization:</label>
                <select class="form-select" bind:value={bulkAutoTrade} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;">
                  <option value="KEEP">— Keep Current Status —</option>
                  <option value="ENABLE">🟢 Enable All Selected (Auto-Trade ON)</option>
                  <option value="DISABLE">⚪ Disable All Selected (Auto-Trade OFF)</option>
                </select>
              </div>

              <div style="margin-top: 10px;">
                <Button fill large color="blue" loading={isBulkSubmitting} onClick={executeBulkConfig}>
                  Apply Bulk Changes to {selectedUccs.size} Client(s)
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Block>
    </Page>
  </Popup>

  <!-- POPUP 3: MANUAL EXTRA SIGNAL GENERATOR (Task 4.6, ExtraManualSignal Parity) -->
  <Popup class="manual-signal-popup" opened={showManualSignalModal} onPopupClosed={() => (showManualSignalModal = false)}>
    <Page>
      <Navbar title="⚡ Extra Manual Signal Generator">
        <NavRight>
          <Link popupClose>Close</Link>
        </NavRight>
      </Navbar>
      <Block>
        <p class="text-color-gray">
          Dispatch a manual trade signal across all subscribed clients. Child orders will be sized independently according to each client's configured Tier × Multiplier.
        </p>
        <div class="card data-table" style="background:#0f172a; border: 1px solid #1e293b;">
          <div class="card-content card-content-padding" style="padding:16px;">
            <div style="display:flex; flex-direction:column; gap:14px;">
              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Scrip Symbol:</label>
                <input type="text" placeholder="e.g. RELIANCE, SBIN, TCS" class="form-input" bind:value={manualSignalSymbol} style="width: 100%; margin-top: 4px; text-transform: uppercase; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;" />
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Action / Direction:</label>
                <div style="display:flex; gap: 8px; margin-top: 4px;">
                  <Button
                    fill={manualSignalAction === 'BUY'}
                    outline={manualSignalAction !== 'BUY'}
                    color="green"
                    style="flex: 1;"
                    onClick={() => (manualSignalAction = 'BUY')}
                  >
                    🟢 BUY
                  </Button>
                  <Button
                    fill={manualSignalAction === 'SELL'}
                    outline={manualSignalAction !== 'SELL'}
                    color="red"
                    style="flex: 1;"
                    onClick={() => (manualSignalAction = 'SELL')}
                  >
                    🔴 SELL
                  </Button>
                </div>
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Strategy Source:</label>
                <select class="form-select" bind:value={manualSignalStrategy} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;">
                  <option value="S1">S1: VWAP Breakout</option>
                  <option value="S2">S2: HML Momentum</option>
                  <option value="S3">S3: Pullback &amp; Mean Reversion</option>
                  <option value="S4">S4: Gap Breakout</option>
                  <option value="DOWN_AVGING">DOWN_AVGING: Staged Matrix</option>
                </select>
              </div>

              <div>
                <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Order Type:</label>
                <select class="form-select" bind:value={manualSignalOrderType} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;">
                  <option value="MARKET">MARKET</option>
                  <option value="LIMIT">LIMIT</option>
                </select>
              </div>

              {#if manualSignalOrderType === 'LIMIT'}
                <div>
                  <label class="item-label font-bold" style="color:#94a3b8; font-size:12px;">Limit Price (₹):</label>
                  <input type="number" step="0.05" placeholder="Limit price" class="form-input" bind:value={manualSignalPrice} style="width: 100%; margin-top: 4px; background:#1e293b; color:#fff; border:1px solid #334155; padding:8px; border-radius:6px;" />
                </div>
              {/if}

              <div style="margin-top: 10px;">
                <Button fill large color="purple" loading={isSendingSignal} onClick={executeManualSignal}>
                  ⚡ Fire Signal to Subscribed Clients
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Block>
    </Page>
  </Popup>
</Page>

<style>
  .row-unassigned-alert {
    background-color: rgba(239, 68, 68, 0.12) !important;
  }
  .row-unassigned-alert:hover {
    background-color: rgba(239, 68, 68, 0.18) !important;
  }

  .bulk-floating-bar {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    border: 1px solid #3b82f6;
    border-radius: 8px;
    padding: 10px 18px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    z-index: 9999;
  }
  .bulk-selected-count {
    color: #f8fafc;
    font-size: 13px;
  }
  .terminal-stats-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 6px 14px;
    gap: 12px;
    flex-wrap: wrap;
  }

  .stats-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .stat-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
  }
  .stat-k {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .stat-v {
    font-size: 13px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    font-family: -apple-system, BlinkMacSystemFont, "SF Mono", "Segoe UI Mono", monospace;
  }

  .text-green { color: #4ade80; }
  .text-red { color: #f87171; }
  .text-blue { color: #60a5fa; }
  .text-cyan { color: #38bdf8; }
  .text-slate { color: #cbd5e1; }

  .stat-divider {
    width: 1px;
    height: 16px;
    background: rgba(148, 163, 184, 0.2);
  }

  .terminal-tabs {
    display: inline-flex;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
  }
  .t-tab {
    border: none;
    background: transparent;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .t-tab:hover {
    color: #f8fafc;
  }
  .t-tab.active {
    background: #3b82f6;
    color: #ffffff;
    box-shadow: 0 0 8px rgba(59, 130, 246, 0.4);
  }

  .auto-trade-btn {
    border: none;
    border-radius: 12px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .auto-trade-btn.active {
    background: #dcfce7;
    color: #15803d;
    border: 1px solid #86efac;
  }
  .auto-trade-btn.inactive {
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #cbd5e1;
  }

  /* Inline Tier Dropdown */
  .inline-tier-select {
    padding: 3px 6px;
    font-size: 11px;
    font-weight: 700;
    border-radius: 4px;
    border: 1px solid #334155;
    background: #1e293b;
    color: #f8fafc;
    cursor: pointer;
    outline: none;
    transition: all 0.15s ease;
  }
  .inline-tier-select.tier-h {
    background: #fee2e2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
  .inline-tier-select.tier-m {
    background: #fef3c7;
    color: #b45309;
    border-color: #fde68a;
  }
  .inline-tier-select.tier-l {
    background: #dcfce7;
    color: #15803d;
    border-color: #86efac;
  }
  .inline-tier-select.tier-custom {
    background: #e0e7ff;
    color: #4338ca;
    border-color: #c7d2fe;
  }
  .inline-tier-select.tier-unassigned {
    background: #fee2e2;
    color: #ef4444;
    border-color: #ef4444;
  }

  /* Quick bulk action buttons */
  .quick-bulk-btn {
    border: 1px solid;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .quick-bulk-btn.tier-h { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
  .quick-bulk-btn.tier-h:hover { background: #fecaca; }
  .quick-bulk-btn.tier-m { background: #fef3c7; color: #b45309; border-color: #fde68a; }
  .quick-bulk-btn.tier-m:hover { background: #fde68a; }
  .quick-bulk-btn.tier-l { background: #dcfce7; color: #15803d; border-color: #86efac; }
  .quick-bulk-btn.tier-l:hover { background: #bbf7d0; }
  .quick-bulk-btn.tier-off { background: #334155; color: #94a3b8; border-color: #475569; }
  .quick-bulk-btn.tier-off:hover { background: #475569; color: #ffffff; }

  /* Directory filter toolbar */
  .directory-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #0f172a;
    border-bottom: 1px solid #1e293b;
    flex-wrap: wrap;
  }
  .dir-search-box {
    background: #1e293b;
    border: 1px solid #334155;
    color: #f8fafc;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 11px;
    min-width: 220px;
  }
  .dir-search-box:focus {
    border-color: #3b82f6;
    outline: none;
  }
  .dir-filter-chips {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-wrap: wrap;
  }
  .dir-chip {
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 12px;
    border: 1px solid #334155;
    background: #1e293b;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .dir-chip:hover {
    color: #ffffff;
    border-color: #475569;
  }
  .dir-chip.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
  }
  .dir-chip.alert-chip {
    border-color: #ef4444;
    color: #f87171;
  }
  .dir-chip.alert-chip.active {
    background: #ef4444;
    color: #ffffff;
  }

  .client-pill-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px dashed #cbd5e1;
    border-radius: 16px;
    padding: 2px 8px 2px 2px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .client-pill-btn:hover {
    background: #f1f5f9;
    border-color: #3b82f6;
  }
  .client-tag-badge {
    background: #1e293b;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    font-family: monospace;
    padding: 2px 6px;
    border-radius: 12px;
    letter-spacing: 0.3px;
  }
  .client-tag-badge.large {
    font-size: 14px;
    padding: 4px 10px;
  }
  .user-subname {
    font-size: 12px;
    color: #334155;
    font-weight: 500;
  }
  .user-subname.unlinked {
    color: #f97316;
    font-style: italic;
  }
  .user-preview-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
  }
  .preview-name {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
  }
  .font-mono {
    font-family: monospace;
  }
  .executive-ticker-strip {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    padding: 0;
    margin-bottom: 12px;
  }
  .ticker-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    color: #334155;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  .ticker-pill.live {
    background: #f0fdf4;
    border-color: #86efac;
    color: #15803d;
  }
  .ticker-pill.testing {
    background: #faf5ff;
    border-color: #d8b4fe;
    color: #6b21a8;
  }
  .ticker-pill.offline {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #b91c1c;
  }
  .pill-label {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.4px;
  }
  .ticker-pill.live .pill-label {
    color: #166534;
  }
  .ticker-pill.testing .pill-label {
    color: #6b21a8;
  }
  .ticker-pill.offline .pill-label {
    color: #991b1b;
  }
  .pill-val {
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.5px;
  }
  .ticker-pill.live .pill-val {
    color: #15803d;
  }
  .ticker-pill.testing .pill-val {
    color: #6b21a8;
  }
  .ticker-pill.offline .pill-val {
    color: #b91c1c;
  }
  .pulse-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    display: inline-block;
  }
  .pulse-dot.dot-live {
    background-color: #22c55e;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    animation: pulse-green 2s infinite;
  }
  .pulse-dot.dot-testing {
    background-color: #a855f7;
    box-shadow: 0 0 0 0 rgba(168, 85, 247, 0.7);
    animation: pulse-purple 2s infinite;
  }
  .pulse-dot.dot-offline {
    background-color: #ef4444;
    box-shadow: none;
    animation: none;
  }
  @keyframes pulse-green {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
</style>
